/** Planner contracts (spec §3.3, §3.4, §4). The JSON shapes match `backend/missions/schemas.py`. */

export type LngLat = [number, number];
export interface XY {
  x: number;
  y: number;
}

export interface Camera {
  sensor_width_mm: number;
  sensor_height_mm: number;
  focal_mm: number;
  image_width_px: number;
  image_height_px: number;
}

/** A row of `GET /api/mission-drones`. */
export interface DroneSpec {
  key: string;
  name: string;
  camera: Camera;
  max_speed_ms: number;
}

export type Trigger = 'interval' | 'waypoint';

export interface MissionInputs {
  polygon: LngLat[];
  margin_m: number;
  altitude_m: number;
  forward_overlap: number;
  side_overlap: number;
  /** Compass bearing of the flight lines, 0 = north–south; null = automatic. */
  angle_deg: number | null;
  trigger: Trigger;
  interval_s: number;
  speed_ms: number;
  hover_s: number;
  terrain_follow: boolean;
  terrain_tolerance_m: number;
  takeoff: LngLat | null;
  /** What the drone does when the controller signal is lost. */
  rc_lost: RcLostAction;
  /** What the drone does after the last waypoint. */
  finish: FinishAction;
  /** Camera pitch at every waypoint, −90 = straight down. */
  gimbal_pitch_deg: number;
}

export type RcLostAction = 'continue' | 'go_home' | 'land' | 'hover';
export const RC_LOST_ACTIONS: RcLostAction[] = ['go_home', 'continue', 'hover', 'land'];
export type FinishAction = 'go_home' | 'hover' | 'land' | 'first_waypoint';
export const FINISH_ACTIONS: FinishAction[] = ['go_home', 'hover', 'land', 'first_waypoint'];

export const DEFAULT_INPUTS: MissionInputs = {
  polygon: [],
  margin_m: 0,
  altitude_m: 110,
  forward_overlap: 80,
  side_overlap: 75,
  angle_deg: null,
  trigger: 'waypoint',
  interval_s: 5,
  speed_ms: 5,
  hover_s: 0,
  terrain_follow: false,
  terrain_tolerance_m: 5,
  takeoff: null,
  rc_lost: 'go_home',
  finish: 'go_home',
  gimbal_pitch_deg: -90,
};

export type WaypointAction = 'takePhoto' | 'hover';

export interface Waypoint {
  lng: number;
  lat: number;
  /** `executeHeight`: metres relative to the takeoff point. */
  height_m: number;
  actions: WaypointAction[];
}

export type PlanWarning = 'speed_capped' | 'clearance_above_120' | 'negative_height' | 'many_waypoints' | 'dtm_unavailable' | 'low_clearance';

export interface PlanStats {
  area_ha: number;
  gsd_cm: number;
  lines: number;
  photos: number;
  path_m: number;
  flight_s: number;
  line_spacing_m: number;
  photo_spacing_m: number;
  clearance_min_m: number | null;
  clearance_max_m: number | null;
}

export interface Profile {
  distance_m: number[];
  /** Ground height relative to the takeoff elevation along the path; null when no DTM covers the mission. */
  terrain_m: (number | null)[] | null;
  /** Flight height relative to the takeoff point. */
  flight_m: number[];
}

export interface Plan {
  version: 1;
  angle_deg: number;
  speed_ms: number;
  lines: [LngLat, LngLat][];
  photos: LngLat[];
  waypoints: Waypoint[];
  profile: Profile;
  photo_gaps_s: number[];
  stats: PlanStats;
  warnings: PlanWarning[];
}

/** Terrain heights above sea level; `null` outside the raster or on no-data. */
export interface Dtm {
  sample(lng: number, lat: number): number | null;
}
